/*
 *  Selectors that define the different variants. The organization variant is based on sector:
 *
 *  COMMON: Health sector organizations (sector === 'HEALTH')
 *  BASE: All other organizations
 *
 *  COMMON has content for the health sector (health-specific asset categories,
 *  professions, network options, etc.)
 */

// KOMMER FRA INNHOLDET ELLER PÅ SERVER
import { createSelector } from '@reduxjs/toolkit';
import type { State } from '../types.js';
import type { OrganizationReference } from '@trinnvis/dabih-api-client';

import { activityCodesAvailableHealth, activityCodesCommon } from './selectors';
import activityCodesRootList from '../data/activity-codes-root.json';
import { professionsForHealthNormalized } from './employees/professions';
import type { ActivityCodeCommon } from './activityCodeCommon';
import type { ActivityCode } from './activityCode';
import { t } from 'src/i18n';

export function getUserOrganization(state: State): OrganizationReference | undefined {
  const path = state.path ?? '';
  const segments = path.split('/');
  if (segments.length >= 3) {
    const id = segments[2];
    const user = state.user;
    if (user?.organizations) {
      return user.organizations.find((x) => x.id === +id);
    }
  }
  return undefined;
}

export const variantName = createSelector(getUserOrganization, (organization) => {
  if (organization !== undefined && organization.sector !== 'HEALTH') {
    return 'BASE';
  }

  return 'COMMON';
});

interface VariantConfiguration {
  activityCodesAvailable: ActivityCode[];
  activityCodesCommon: ActivityCodeCommon[];
  professions: string[];
  hideProfessions: boolean;
  hideShareVacation: boolean;
  hideActivityCodes: boolean;
  simplifiedIssuesChecklist: boolean;
  simplifiedAssets: boolean;
  simplifiedPrivacy: boolean;
  networkView: {
    firewallTooltip: string;
    connectionTypes: {
      id: string;
      value: string;
    }[];
    firewallOptions: {
      id: string;
      value: string;
    }[];
  };
  personalDataItemTypes: {
    id: string;
    value: string;
  }[];
  assetsView: {
    categories: string[];
  };
  staffingPage: {
    groupingPrompt: string;
  };
}

interface VariantConfigurations {
  BASE: VariantConfiguration;
  COMMON: VariantConfiguration;
}

function activityCodesRoot(): ActivityCode[] {
  return activityCodesRootList;
}

function activityCodesRootCommon(): ActivityCodeCommon[] {
  const commonCodes = ['A', 'C', 'D', 'F', 'G', 'H', 'I', 'J', 'L'];
  return activityCodesRootList
    .filter((x) => commonCodes.includes(x.code))
    .map((x) => {
      return {
        name: x.name,
        code: [x.code],
      };
    });
}

function getVariantConfigurations(): VariantConfigurations {
  return {
    BASE: {
      activityCodesAvailable: activityCodesRoot(),
      activityCodesCommon: activityCodesRootCommon(),
      professions: ['Annet'],
      hideProfessions: true,
      hideActivityCodes: true,
      hideShareVacation: true,
      simplifiedPrivacy: true,
      simplifiedIssuesChecklist: true,
      simplifiedAssets: true,
      networkView: {
        firewallTooltip: '',
        firewallOptions: [
          { id: 'NONE', value: t('computers.options.none') },
          { id: 'OTHER', value: t('variants.firewallOptions.otherSpecify') },
        ],
        connectionTypes: [
          { id: 'NONE', value: t('computers.options.noInternetConnection') },
          { id: 'OTHER', value: t('variants.connectionTypes.connectedToInternet') },
        ],
      },
      personalDataItemTypes: [
        { id: 'UNDEFINED', value: t('computers.options.chooseType') },
        { id: 'employeeRecords', value: t('variants.personalDataItemTypes.employeeRecords') },
        { id: 'customerRecords', value: t('variants.personalDataItemTypes.customerRecords') },
        { id: 'OTHER', value: t('variants.personalDataItemTypes.other') },
      ],
      assetsView: {
        categories: ['Ingen', 'Produksjon', 'Annet'],
      },
      staffingPage: {
        groupingPrompt: t('variants.staffingPage.groupingPromptBase'),
      },
    },
    COMMON: {
      activityCodesAvailable: activityCodesAvailableHealth(),
      activityCodesCommon: activityCodesCommon(),
      professions: professionsForHealthNormalized,
      hideProfessions: false,
      hideShareVacation: false,
      hideActivityCodes: false,
      simplifiedPrivacy: false,
      simplifiedIssuesChecklist: false,
      simplifiedAssets: false,
      networkView: {
        firewallTooltip: t('variants.firewallTooltipHealth'),
        firewallOptions: [
          { id: 'NONE', value: t('computers.options.none') },
          { id: 'NHN', value: t('computers.options.firewallNhnNhnLine') },
          { id: 'NHNotherISP', value: t('computers.options.firewallNhnOwnLine') },
          { id: 'OTHER', value: t('computers.options.firewallOtherSpecify') },
        ],
        connectionTypes: [
          { id: 'NONE', value: t('computers.options.noInternetConnection') },
          {
            id: 'NHN',
            value: t('computers.options.nhnAsIsp'),
          },
          {
            id: 'NHNotherISP',
            value: t('computers.options.nhnOwnIsp'),
          },
          { id: 'OTHER', value: t('computers.options.internetOutsideNhn') },
        ],
      },
      personalDataItemTypes: [
        { id: 'UNDEFINED', value: t('computers.options.chooseType') },
        { id: 'medicalRecords', value: t('variants.personalDataItemTypes.medicalRecords') },
        {
          id: 'medicalEquipmentRecords',
          value: t('variants.personalDataItemTypes.medicalEquipmentRecords'),
        },
        { id: 'patientDialogue', value: t('variants.personalDataItemTypes.patientDialogue') },
        { id: 'employeeRecords', value: t('variants.personalDataItemTypes.employeeRecords') },
        { id: 'customerRecords', value: t('variants.personalDataItemTypes.customerRecords') },
        { id: 'OTHER', value: t('variants.personalDataItemTypes.other') },
      ],
      assetsView: {
        categories: ['Ingen', 'Diagnostisk utstyr', 'Behandlingsutstyr', 'Treningsutstyr', 'Annet'],
      },
      staffingPage: {
        groupingPrompt: t('variants.staffingPage.groupingPromptHealth'),
      },
    },
  };
}

export const variantConfiguration = createSelector(variantName, (name) => {
  return getVariantConfigurations()[name as keyof VariantConfigurations];
});

export const variantSimplifiedPrivacy = createSelector(variantConfiguration, (v) => {
  return v.simplifiedPrivacy;
});

export const variantAssetsView = createSelector(variantConfiguration, (v) => {
  return v.assetsView;
});

export const variantSimplifiedAssets = createSelector(variantConfiguration, (v) => {
  return v.simplifiedAssets;
});
